import { useRef, useState, useEffect, useCallback } from "react";

interface ParallaxImageProps {
  src: string;
  alt: string;
  className?: string;
}

const ParallaxImage = ({ src, alt, className = "" }: ParallaxImageProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState({ rotateX: 0, rotateY: 0, scale: 1 });
  const gyroActive = useRef(false);

  // Auto-activate gyroscope silently
  useEffect(() => {
    const handleOrientation = (e: DeviceOrientationEvent) => {
      gyroActive.current = true;
      const beta = Math.max(-45, Math.min(45, e.beta ?? 0));
      const gamma = Math.max(-45, Math.min(45, e.gamma ?? 0));
      setTransform({
        rotateX: (beta - 20) * 0.35,
        rotateY: gamma * 0.35,
        scale: 1,
      });
    };

    const init = async () => {
      if (typeof (DeviceOrientationEvent as any).requestPermission === "function") {
        try {
          const perm = await (DeviceOrientationEvent as any).requestPermission();
          if (perm === "granted") window.addEventListener("deviceorientation", handleOrientation);
        } catch { /* silent */ }
      } else {
        window.addEventListener("deviceorientation", handleOrientation);
      }
    };

    init();
    return () => window.removeEventListener("deviceorientation", handleOrientation);
  }, []);

  // Scroll-based parallax (fallback when no gyro)
  useEffect(() => {
    const handleScroll = () => {
      if (gyroActive.current) return;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const offset = ((rect.top + rect.height / 2) / window.innerHeight - 0.5) * 2;
      setTransform({ rotateX: offset * 12, rotateY: 0, scale: 1 });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Desktop mouse
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTransform({ rotateX: -y * 18, rotateY: x * 18, scale: 1.02 });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTransform({ rotateX: 0, rotateY: 0, scale: 1 });
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      style={{ perspective: "800px" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="w-full overflow-hidden rounded-2xl shadow-2xl will-change-transform"
        style={{
          transform: `rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg) scale(${transform.scale})`,
          transformStyle: "preserve-3d",
          transition: gyroActive.current ? "transform 0.08s linear" : "transform 0.3s ease-out",
        }}
      >
        <img src={src} alt={alt} className="h-full w-full object-cover" draggable={false} />
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl"
          style={{
            background: `radial-gradient(circle at ${50 + transform.rotateY * 2.5}% ${50 - transform.rotateX * 2.5}%, rgba(255,255,255,0.18) 0%, transparent 55%)`,
          }}
        />
      </div>
    </div>
  );
};

export default ParallaxImage;
